/**
 * AuthContext - Backward Compatibility Wrapper
 * 
 * This Context now wraps authStore (Zustand) to maintain backward compatibility
 * with existing components that use useAuth() hook.
 * 
 * Migration Strategy:
 * - Phase 2.3.2: This wrapper allows existing code to work unchanged
 * - Phase 2.3.9: Components will be refactored to use useAuthStore directly
 * - After migration: This wrapper can be removed
 * 
 * New code should use: import useAuthStore from './stores/authStore'
 * Legacy code can still use: import { useAuth } from './contexts/AuthContext'
 * 
 * See docs/ZUSTAND_MIGRATION_GUIDE.md for details.
 */

import React, { createContext, useContext } from 'react';
import useAuthStore from '../stores/authStore';

const AuthContext = createContext(null);

/**
 * AuthProvider - Wrapper component that provides auth state via Context
 * 
 * This is now a thin wrapper around Zustand authStore.
 * It subscribes to the store and provides values via Context for backward compatibility.
 */
export const AuthProvider = ({ children }) => {
  // Subscribe to ALL auth store state for Context consumers
  // Note: This causes re-renders when ANY auth state changes, which is why
  // we're migrating away from Context. New code should use useAuthStore with
  // selective subscriptions.
  const authState = useAuthStore();

  // The Context value matches the old AuthContext API exactly
  const value = {
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    isLoadingProfile: authState.isLoadingProfile,
    user: authState.user,
    error: authState.error,
    login: authState.login,
    logout: authState.logout,
    handleCallback: authState.handleCallback,
    getAccessToken: authState.getAccessToken,
    getTokenExpiresAt: authState.getTokenExpiresAt,
    refreshUserProfile: authState.fetchUserProfile, // API compatibility
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth - Hook to access auth context
 * 
 * @deprecated Use useAuthStore from '../stores/authStore' instead for better performance
 * 
 * This hook is kept for backward compatibility during migration.
 * It works exactly like before, but now uses Zustand internally.
 * 
 * Example migration:
 * 
 * OLD (Context - causes re-renders):
 * ```javascript
 * import { useAuth } from './contexts/AuthContext';
 * const { user, isLoading } = useAuth();
 * ```
 * 
 * NEW (Zustand - selective subscriptions):
 * ```javascript
 * import useAuthStore from './stores/authStore';
 * const user = useAuthStore(state => state.user);
 * const isLoading = useAuthStore(state => state.isLoading);
 * ```
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Hook for components that only need authentication status
 * More efficient than useAuth() when you don't need all auth state
 * 
 * @example
 * const isAuthenticated = useIsAuthenticated();
 */
export const useIsAuthenticated = () => {
  return useAuthStore(state => state.isAuthenticated);
};

/**
 * Hook for components that only need user data
 * More efficient than useAuth() when you don't need auth flow functions
 * 
 * @example
 * const user = useUser();
 */
export const useUser = () => {
  return useAuthStore(state => state.user);
};

/**
 * Hook for components that need auth actions without state
 * Doesn't cause re-renders when auth state changes
 * 
 * @example
 * const { login, logout } = useAuthActions();
 */
export const useAuthActions = () => {
  return useAuthStore(state => ({
    login: state.login,
    logout: state.logout,
    handleCallback: state.handleCallback,
    getAccessToken: state.getAccessToken,
  }));
};
